import { PHASE_COLOURS, PHASE_NAMES, SERIES_COLOURS, parameterById, plotByKind } from './registry';
import { eventClassificationFromAnalysis, eventClassificationLabel } from './event-analysis';
import { formatUtc } from './time';
import { formatEventDuration } from './tooltip-format';
import type { ApiEnvelope, ChartModel, ChartSeries, PlotSpec } from './types';

const asRows = (value: unknown): unknown[][] => Array.isArray(value) ? value.filter(Array.isArray) as unknown[][] : [];
const numeric = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;

function timestampedLineData(rows: unknown[][], valueIndex: number): Array<[number, number | null]> {
  return rows.flatMap((row) => {
    const timestamp = numeric(row[0]);
    return timestamp == null ? [] : [[timestamp, numeric(row[valueIndex])]];
  });
}

function timestampedRangeData(rows: unknown[][], lowIndex: number, highIndex: number): Array<[number, number | null, number | null]> {
  return rows.flatMap((row): Array<[number, number | null, number | null]> => {
    const timestamp = numeric(row[0]);
    const low = numeric(row[lowIndex]);
    const high = numeric(row[highIndex]);
    if (timestamp == null) return [];
    if (low == null || high == null) return [[timestamp, null, null]];
    return [[timestamp, low, high]];
  });
}

function measurementUnit(plot: PlotSpec): string {
  const parameter = parameterById(plot.parameterId);
  if (plot.options.perUnit && (plot.kind === 'harmonic-spectrum' || plot.kind === 'harmonic-trend')) return '%';
  return parameter.units;
}

function unitsLabel(plot: PlotSpec): string {
  const parameter = parameterById(plot.parameterId);
  const unit = measurementUnit(plot);
  return unit ? `${parameter.name} (${unit})` : parameter.name;
}

function title(plot: PlotSpec): string {
  return `${parameterById(plot.parameterId).name} — ${plotByKind(plot.kind).shortName}`;
}

function lineSeries(name: string, data: unknown[], index: number, colours = PHASE_COLOURS): ChartSeries {
  return { name, data, color: colours[index % colours.length], lineWidth: 1.5, marker: { enabled: false } };
}

export function chartModelFor(plot: PlotSpec, response: ApiEnvelope): ChartModel {
  const selectedParameter = parameterById(plot.parameterId);
  const rows = asRows(response.data);
  const useRmsLimits = plot.kind === 'average-series' || plot.kind === 'phase-series';
  const base: ChartModel = {
    title: title(plot),
    yAxisTitle: unitsLabel(plot),
    valueSuffix: measurementUnit(plot) || undefined,
    xAxisType: 'datetime',
    stableEventLegend: plotByKind(plot.kind).supportsEvents,
    series: [],
    yAxisOptions: useRmsLimits ? {
      min: selectedParameter.minimum,
      plotBands: selectedParameter.plotBands?.map((band) => ({
        from: band.from,
        to: band.to,
        color: band.tone === 'danger' ? 'rgba(180, 33, 38, 0.25)' : 'rgba(222, 215, 20, 0.22)',
        label: { text: band.label, style: { color: '#68747c' } }
      }))
    } : {}
  };

  if (plot.kind === 'average-series') {
    const average = timestampedLineData(rows, 1);
    const range = timestampedRangeData(rows, 2, 3);
    return {
      ...base,
      series: [
        lineSeries('Average', average, 0, SERIES_COLOURS),
        { name: 'Min-max range', type: 'arearange', data: range, color: '#7cb5ec', fillOpacity: 0.22, lineWidth: 0, linkedTo: ':previous', enableMouseTracking: false, showInLegend: true, zIndex: 0 }
      ],
      emptyMessage: rows.length ? undefined : 'No measurements in this range.'
    };
  }

  if (plot.kind === 'phase-series') {
    const columns = response.columns?.slice(1) ?? [];
    const visibleColumnCount = plot.options.showNeutral ? columns.length : Math.min(3, columns.length);
    return {
      ...base,
      series: columns.slice(0, visibleColumnCount).map((column, columnIndex) => lineSeries(
        PHASE_NAMES[columnIndex] ?? column,
        timestampedLineData(rows, columnIndex + 1),
        columnIndex
      )),
      emptyMessage: rows.length ? undefined : 'No individual-phase measurements in this range.'
    };
  }

  if (plot.kind === 'day-polar') {
    const columns = response.columns?.slice(1) ?? [];
    const visibleColumnCount = plot.options.showNeutral ? columns.length : Math.min(3, columns.length);
    return {
      ...base,
      yAxisOptions: { min: selectedParameter.minimum },
      polar: true,
      chartType: 'line',
      xAxisType: 'linear',
      xAxisTitle: 'Hour (UTC)',
      series: columns.slice(0, visibleColumnCount).map((column, columnIndex) => lineSeries(
        PHASE_NAMES[columnIndex] ?? column,
        rows.flatMap((row) => {
          const timestamp = numeric(row[0]);
          const value = numeric(row[columnIndex + 1]);
          if (timestamp == null || value == null) return [];
          const date = new Date(timestamp);
          return [[date.getUTCHours() + date.getUTCMinutes() / 60, value]];
        }),
        columnIndex
      )),
      emptyMessage: rows.length ? undefined : 'No measurements on this UTC day.'
    };
  }

  if (plot.kind === 'heatmap') {
    return {
      ...base,
      chartType: 'heatmap',
      xAxisTitle: 'Date (UTC)',
      yAxisTitle: 'Hour (UTC)',
      xAxisOptions: { title: { text: 'Days (UTC)' } },
      yAxisOptions: {
        min: 0,
        max: 23,
        reversed: true,
        minPadding: 0,
        maxPadding: 0,
        startOnTick: false,
        endOnTick: false,
        tickPositions: [0, 6, 12, 18, 23]
      },
      colorAxis: {
        min: parameterById(plot.parameterId).minimum,
        stops: [[0, '#3060cf'], [0.5, '#fffbbc'], [0.9, '#c4463a'], [1, '#c4463a']],
        startOnTick: false,
        endOnTick: false
      },
      series: [{
        name: `${parameterById(plot.parameterId).name} (average)`,
        type: 'heatmap',
        data: rows,
        borderWidth: 0,
        colsize: 24 * 36e5,
        nullColor: '#efefef'
      }],
      emptyMessage: rows.length ? undefined : 'No heat-map measurements are available.'
    };
  }

  if (plot.kind === 'harmonic-spectrum') {
    const matrix = asRows(response.data);
    const orders = (matrix[0] ?? []).filter((item): item is number => typeof item === 'number');
    return {
      ...base,
      subtitle: resolvedSubtitle(response),
      chartType: 'column',
      xAxisType: 'linear',
      xAxisTitle: 'Harmonic order',
      series: matrix.slice(1).map((values, index) => lineSeries(
        PHASE_NAMES[index] ?? `Series ${index + 1}`,
        orders.flatMap((order, itemIndex) => numeric(values[itemIndex]) != null ? [[order, values[itemIndex]]] : []),
        index
      )),
      emptyMessage: matrix.length > 1 && orders.length ? undefined : gapMessage(response)
    };
  }

  if (plot.kind === 'waveform') {
    const matrix = asRows(response.data);
    return {
      ...base,
      subtitle: resolvedSubtitle(response),
      xAxisType: 'linear',
      xAxisTitle: 'Time (ms)',
      series: matrix.map((values, index) => lineSeries(
        PHASE_NAMES[index] ?? `Series ${index + 1}`,
        // Preserve the legacy reconstruction domain: 400 samples at 0.1 ms
        // cover two 50 Hz cycles (0.0 through 39.9 ms).
        values.flatMap((value, itemIndex) => numeric(value) != null ? [[values.length ? itemIndex * 40 / values.length : 0, value]] : []),
        index
      )),
      emptyMessage: matrix.some((values) => values.length > 0) ? undefined : gapMessage(response)
    };
  }

  const matrix = asRows(response.data);
  const timestamps = (matrix[0] ?? []).filter((item): item is number => typeof item === 'number');
  const harmonicNumbers = response.harmonic_numbers ?? [];
  return {
    ...base,
    series: matrix.slice(1).flatMap((values, index) => {
      const harmonic = harmonicNumbers[index] ?? index + 1;
      if (harmonic === 1 && plot.options.showFundamental === false) return [];
      return [lineSeries(
        `Harmonic ${harmonic}`,
        timestamps.flatMap((timestamp, itemIndex) => numeric(values[itemIndex]) != null ? [[timestamp, values[itemIndex]]] : []),
        index,
        SERIES_COLOURS
      )];
    }),
    emptyMessage: timestamps.length ? undefined : 'No harmonic trend measurements in this range.'
  };
}

function resolvedSubtitle(response: ApiEnvelope): string | undefined {
  const resolved = response.navigation?.resolved_timestamp;
  return resolved == null ? undefined : `Resolved sample: ${formatUtc(resolved, true)}`;
}

function gapMessage(response: ApiEnvelope): string {
  const tolerance = response.navigation?.tolerance_seconds ?? 450;
  return `No common three-phase sample exists within ${tolerance} seconds of the requested time.`;
}

export function histogramModel(plot: PlotSpec, response: ApiEnvelope): ChartModel | null {
  if (!plot.options.showHistogram) return null;
  const rows = asRows(response.data);
  const availableColumnCount = Math.max(0, Math.max(0, ...rows.map((row) => row.length - 1)));
  const columnCount = plot.kind === 'average-series'
    ? 1
    : plot.options.showNeutral ? availableColumnCount : Math.min(3, availableColumnCount);
  const columns = Array.from({ length: columnCount }, (_, index) => ({
    name: plot.kind === 'average-series' ? 'Average' : (PHASE_NAMES[index] ?? response.columns?.[index + 1] ?? `Series ${index + 1}`),
    values: rows.flatMap((row) => numeric(row[index + 1]) == null ? [] : [row[index + 1] as number]),
    color: plot.kind === 'average-series' ? SERIES_COLOURS[0] : PHASE_COLOURS[index % PHASE_COLOURS.length]
  })).filter((column) => column.values.length > 0);
  if (columns.length === 0) return { title: 'Histogram', xAxisType: 'linear', series: [], emptyMessage: 'No values to summarise.' };

  const binSeries = columns.map((column) => {
    const dataMinimum = Math.min(...column.values);
    const minimum = parameterById(plot.parameterId).minimum ?? dataMinimum;
    const maximum = Math.max(...column.values);
    const binCount = 40;
    const width = maximum === minimum ? 1 : (maximum - minimum) / binCount;
    const counts = Array.from({ length: binCount }, () => 0);
    for (const value of column.values) counts[Math.min(binCount - 1, Math.max(0, Math.floor((value - minimum) / width)))] += 1;
    if (plot.options.cumulative) {
      for (let index = 1; index < counts.length; index += 1) counts[index] += counts[index - 1];
    }
    return {
      name: column.name,
      type: 'area',
      color: column.color,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
      data: counts.map((count, index) => [minimum + (index + 0.5) * width, 100 * count / column.values.length])
    } as ChartSeries;
  });
  return {
    title: plot.options.cumulative ? 'Cumulative histogram' : 'Histogram',
    xAxisType: 'linear',
    xAxisTitle: unitsLabel(plot),
    yAxisTitle: '%',
    chartType: 'area',
    inverted: true,
    legendEnabled: false,
    tooltipEnabled: false,
    xAxisOptions: { min: parameterById(plot.parameterId).minimum, reversed: true },
    yAxisOptions: { min: 0, max: plot.options.cumulative ? 100 : undefined },
    series: binSeries
  };
}

const EVENT_STYLES = [
  { label: 'NOP_opened', name: 'NOP opened', color: 'green', symbol: 'circle', y: 80 },
  { label: 'NOP_closed', name: 'NOP closed', color: 'green', symbol: 'square', y: 70 },
  { label: 'Voltage Swell', name: 'Voltage rise', color: 'rgba(254, 178, 76, 0.8)', symbol: 'triangle', y: 60 },
  { label: 'Voltage Sag', name: 'Voltage dip', color: 'rgba(254, 178, 76, 0.8)', symbol: 'triangle-down', y: 50 },
  { label: 'Interruption', name: 'Interruption', color: 'rgba(254, 178, 76, 0.8)', symbol: 'triangle-down', y: 45 },
  { label: 'Over-frequency', name: 'Over-frequency', color: 'rgba(36, 78, 198, 0.8)', symbol: 'triangle', y: 40 },
  { label: 'Under-frequency', name: 'Under-frequency', color: 'rgba(36, 78, 198, 0.8)', symbol: 'triangle-down', y: 30 },
  { label: 'Phase Current Trigger', name: 'Phase over-current', color: 'rgba(180, 33, 38, 0.8)', symbol: 'triangle', y: 20 },
  { label: 'Neutral Current Trigger', name: 'Neutral over-current', color: 'rgba(180, 33, 38, 0.8)', symbol: 'triangle', y: 10 },
  { label: 'Waveshape Change', name: 'Waveshape change', color: 'rgba(117, 107, 177, 0.8)', symbol: 'diamond', y: 0 }
] as const;

export interface EventMarkerAnalysis {
  analysis_status?: string;
  estimated_cause?: string;
}

export interface EventMarkerItem {
  t: number;
  duration_ms?: number;
  analysis?: EventMarkerAnalysis;
}

export interface EventClassificationOption {
  key: string;
  label: string;
}

export interface EventClassificationChoice extends EventClassificationOption {
  count: number;
}

const UNCLASSIFIED_EVENT: EventClassificationOption = {
  key: 'status:not-classified',
  label: 'Not classified'
};

const EVENT_CLASSIFICATION_ORDER = new Map([
  'Voltage rise',
  'Upstream voltage dip',
  'Fault-induced voltage dip',
  'Voltage dip',
  'Incipient fault',
  'Single-phase fault',
  'Transient phase-to-phase fault',
  'Phase over-current',
  'Fault-like disturbance',
  'Interruption',
  'Neutral over-current',
  'Switching transient',
  'Motor energisation',
  'Waveshape change',
  'Load increase',
  'Load decrease',
  'Over Frequency',
  'Under frequency',
  'No event'
].map((label, index) => [label, index]));

const NO_EVENT_MARKER_COLOUR = '#a9686b';
const EVENT_MARKER_RADIUS = 6;
const EVENT_MARKER_HOVER_RADIUS_PLUS = 3;
const EVENT_MARKER_HIGHLIGHT_RADIUS = EVENT_MARKER_RADIUS + EVENT_MARKER_HOVER_RADIUS_PLUS;
const EVENT_MARKER_OUTLINE_COLOUR = '#ffffff';
// Highcharts 4 owns one transient halo per series, so a halo cannot represent
// both a persistent displayed event and an independently hovered marker. Keep
// both states halo-free and share the same enlarged, outlined marker styling.
const EVENT_MARKER_HALO = { size: 0, opacity: 0 };

function tooltipText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function eventClassificationForMarker(analysis: unknown): EventClassificationOption {
  return eventClassificationFromAnalysis(analysis) ?? UNCLASSIFIED_EVENT;
}

function eventMarkerIsInRange(
  item: EventMarkerItem,
  visibleRangeSeconds?: [number, number]
): boolean {
  return !visibleRangeSeconds
    || (item.t >= visibleRangeSeconds[0] * 1000 && item.t <= visibleRangeSeconds[1] * 1000);
}

export function eventClassificationOptions(
  events: Record<string, EventMarkerItem[]>,
  visibleRangeSeconds?: [number, number]
): EventClassificationChoice[] {
  const classifications = new Map<string, EventClassificationChoice>();
  for (const items of Object.values(events)) {
    for (const item of items) {
      const classification = eventClassificationForMarker(item.analysis);
      const existing = classifications.get(classification.key);
      classifications.set(classification.key, {
        ...classification,
        count: (existing?.count ?? 0) + (eventMarkerIsInRange(item, visibleRangeSeconds) ? 1 : 0)
      });
    }
  }
  return Array.from(classifications.values())
    .sort((left, right) => {
      const leftOrder = EVENT_CLASSIFICATION_ORDER.get(left.label);
      const rightOrder = EVENT_CLASSIFICATION_ORDER.get(right.label);
      if (leftOrder !== undefined || rightOrder !== undefined) {
        return (leftOrder ?? Number.MAX_SAFE_INTEGER) - (rightOrder ?? Number.MAX_SAFE_INTEGER);
      }
      return left.label.localeCompare(right.label, 'en-GB');
    });
}

export function selectableEventTimestamps(
  events: Record<string, EventMarkerItem[]>,
  visibleRangeSeconds?: [number, number],
  hiddenClassifications: ReadonlySet<string> = new Set()
): number[] {
  const timestamps = new Set<number>();
  for (const items of Object.values(events)) {
    for (const item of items) {
      if (
        (item.duration_ms ?? 0) <= 0
        || !eventMarkerIsInRange(item, visibleRangeSeconds)
        || hiddenClassifications.has(eventClassificationForMarker(item.analysis).key)
      ) continue;
      const timestamp = Math.round(item.t / 1000);
      if (Number.isSafeInteger(timestamp) && timestamp >= 0) timestamps.add(timestamp);
    }
  }
  return Array.from(timestamps).sort((left, right) => left - right);
}

export function appendEventMarkers(
  model: ChartModel,
  events: Record<string, EventMarkerItem[]>,
  onEvent?: (timestampMilliseconds: number, eventType: string) => void,
  visibleRangeSeconds?: [number, number],
  hiddenClassifications: ReadonlySet<string> = new Set()
): ChartModel {
  const known = new Set(EVENT_STYLES.map((style) => style.label));
  const styles = [
    ...EVENT_STYLES,
    ...Object.keys(events).filter((label) => !known.has(label as typeof EVENT_STYLES[number]['label'])).map((label, index) => ({
      label, name: label, color: SERIES_COLOURS[index % SERIES_COLOURS.length], symbol: 'triangle', y: Math.max(0, 80 - index * 10)
    }))
  ];
  const markers = styles.flatMap((style) => {
    const items = (events[style.label] ?? []).filter((item) => {
      return eventMarkerIsInRange(item, visibleRangeSeconds)
        && !hiddenClassifications.has(eventClassificationForMarker(item.analysis).key);
    });
    const points = items.map((item) => {
      const classification = eventClassificationLabel(item.analysis);
      const noEvent = item.analysis?.analysis_status === 'classified'
        && item.analysis.estimated_cause === 'no_event';
      const pointHighlightState = noEvent
        ? { fillColor: NO_EVENT_MARKER_COLOUR }
        : undefined;
      return {
        x: item.t,
        y: style.y,
        duration_ms: item.duration_ms ?? 0,
        duration_display: formatEventDuration(item.duration_ms ?? 0),
        classification,
        classification_suffix: classification
          ? `<br>Classification: ${tooltipText(classification)}`
          : '',
        color: noEvent ? NO_EVENT_MARKER_COLOUR : undefined,
        marker: pointHighlightState ? {
          states: { hover: pointHighlightState, select: pointHighlightState }
        } : undefined
      };
    });
    const markerHighlightState = {
      radius: EVENT_MARKER_HIGHLIGHT_RADIUS,
      lineWidth: 1,
      lineColor: EVENT_MARKER_OUTLINE_COLOUR,
      fillColor: style.color
    };
    return points.length ? [{
      name: style.name,
      type: 'scatter',
      data: points,
      color: style.color,
      marker: {
        enabled: true,
        symbol: style.symbol,
        radius: EVENT_MARKER_RADIUS,
        states: {
          hover: markerHighlightState,
          select: markerHighlightState
        }
      },
      states: {
        hover: { halo: EVENT_MARKER_HALO },
        select: { halo: EVENT_MARKER_HALO }
      },
      yAxis: 1,
      cursor: 'pointer',
      stickyTracking: false,
      zIndex: 6,
      tooltip: { pointFormat: '<b>{series.name}</b><br>{point.x:%H:%M:%S}<br>Duration: {point.duration_display}{point.classification_suffix}' },
      point: { events: { click: function (this: { x: number; duration_ms: number }) { if (this.duration_ms > 0) onEvent?.(this.x, style.label); } } }
    } as ChartSeries] : [];
  });
  return { ...model, series: [...model.series, ...markers] };
}

export interface EventWaveformResponse {
  Microseconds_Per_Sample?: number;
  Samples_Per_Cycle?: number;
  Trigger_Sample_Number?: number;
  Milliseconds?: number[];
  L1_N__V?: number[];
  L2_N__V?: number[];
  L3_N__V?: number[];
  N_E__V?: number[];
  L1_Amp__A?: number[];
  L2_Amp__A?: number[];
  L3_Amp__A?: number[];
  N_Amp__A?: number[];
  two_part?: boolean;
  analysis?: unknown;
  analysis_status?: unknown;
}

const EVENT_SEGMENT_SAMPLES = 1024;
const EVENT_WAVEFORM_Y_AXIS_OPTIONS = {
  minPadding: 0.02,
  maxPadding: 0.02,
  startOnTick: false,
  endOnTick: false
};

function eventSampleIntervalMilliseconds(data: EventWaveformResponse): number {
  const recordedInterval = Number(data.Microseconds_Per_Sample) / 1000;
  if (Number.isFinite(recordedInterval) && recordedInterval > 0) return recordedInterval;

  const differences = (data.Milliseconds ?? []).slice(1).flatMap((value, index) => {
    const difference = value - (data.Milliseconds?.[index] ?? value);
    return Number.isFinite(difference) && difference > 0 ? [difference] : [];
  }).sort((left, right) => left - right);
  if (!differences.length) return 1;
  const middle = Math.floor(differences.length / 2);
  const median = differences.length % 2
    ? differences[middle]
    : (differences[middle - 1] + differences[middle]) / 2;
  const continuous = differences.filter((difference) => difference <= median * 2);
  const continuousMiddle = Math.floor(continuous.length / 2);
  return continuous.length % 2
    ? continuous[continuousMiddle]
    : (continuous[continuousMiddle - 1] + continuous[continuousMiddle]) / 2;
}

export function formatEventElapsedTime(milliseconds: number): string {
  if (!Number.isFinite(milliseconds)) return 'Unavailable';
  return formatEventDuration(milliseconds);
}

export function eventWaveformModels(data: EventWaveformResponse, timestampSeconds: number, showNeutral = false): ChartModel[] {
  const milliseconds = data.Milliseconds ?? [];
  const sampleIntervalMilliseconds = eventSampleIntervalMilliseconds(data);
  const isSplit = data.two_part === true && milliseconds.length > EVENT_SEGMENT_SAMPLES;
  const samplesPerCycle = Number.isFinite(data.Samples_Per_Cycle) && Number(data.Samples_Per_Cycle) > 0
    ? Math.round(Number(data.Samples_Per_Cycle))
    : undefined;
  const firstSegmentLength = isSplit ? EVENT_SEGMENT_SAMPLES : milliseconds.length;
  const timestampTriggerSample = milliseconds.slice(0, firstSegmentLength).reduce(
    (closest, value, index) => Math.abs(value) < Math.abs(milliseconds[closest] ?? Number.POSITIVE_INFINITY) ? index : closest,
    0
  );
  const triggerSample = milliseconds.length
    ? timestampTriggerSample
    : Number.isFinite(data.Trigger_Sample_Number) && Number(data.Trigger_Sample_Number) >= 0
      ? Math.round(Number(data.Trigger_Sample_Number))
      : undefined;
  const build = (name: string, units: string, arrays: Array<number[] | undefined>): ChartModel => {
    const visibleArrays = showNeutral ? arrays : arrays.slice(0, 3);
    const series = visibleArrays.flatMap((values, index) => {
      if (!values?.length) return [];
      const first = {
        ...lineSeries(PHASE_NAMES[index], isSplit ? values.slice(0, EVENT_SEGMENT_SAMPLES) : values, index),
        pointStart: 0,
        pointInterval: sampleIntervalMilliseconds
      };
      if (!isSplit) return [first];
      return [first, {
        ...lineSeries(PHASE_NAMES[index], values.slice(EVENT_SEGMENT_SAMPLES), index),
        pointStart: EVENT_SEGMENT_SAMPLES * sampleIntervalMilliseconds,
        pointInterval: sampleIntervalMilliseconds,
        linkedTo: ':previous',
        showInLegend: false
      }];
    });
    return {
      title: `${name} event waveform`,
      subtitle: formatUtc(timestampSeconds, true),
      xAxisType: 'linear',
      xAxisTitle: 'Time (ms)',
      yAxisTitle: `${name} (${units})`,
      yAxisOptions: EVENT_WAVEFORM_Y_AXIS_OPTIONS,
      waveformMetadata: { sampleIntervalMs: sampleIntervalMilliseconds, samplesPerCycle, triggerSample },
      valueSuffix: units,
      tooltipEnabled: true,
      xAxisOptions: {
        title: { text: null },
        ...(isSplit ? {} : { labels: { enabled: false }, minorTickLength: 0, tickLength: 0 })
      },
      xAxisDiscontinuity: isSplit ? {
        before: (EVENT_SEGMENT_SAMPLES - 1) * sampleIntervalMilliseconds,
        after: EVENT_SEGMENT_SAMPLES * sampleIntervalMilliseconds,
        startLabel: '0 ms',
        resumeLabel: formatEventElapsedTime(milliseconds[EVENT_SEGMENT_SAMPLES])
      } : undefined,
      series,
      emptyMessage: milliseconds.length ? undefined : 'No captured event waveform is available.'
    };
  };
  return [
    build('Voltage', 'V', [data.L1_N__V, data.L2_N__V, data.L3_N__V, data.N_E__V]),
    build('Current', 'A', [data.L1_Amp__A, data.L2_Amp__A, data.L3_Amp__A, data.N_Amp__A])
  ];
}

export function eventWaveformAnalysisModel(models: ChartModel[], emptyMessage?: string): ChartModel {
  return {
    title: 'Captured event waveform analysis',
    xAxisType: 'linear',
    xAxisTitle: 'Time (ms)',
    yAxisOptions: EVENT_WAVEFORM_Y_AXIS_OPTIONS,
    waveformMetadata: models.find((model) => model.waveformMetadata)?.waveformMetadata,
    series: models.flatMap((model) => {
      const quantity = model.title.replace(/\s+event waveform$/i, '');
      return model.series.map((series) => ({
        ...series,
        name: `${quantity}: ${series.name}`,
        tooltip: { ...(series.tooltip ?? {}), valueSuffix: model.valueSuffix ?? '' }
      }));
    }),
    emptyMessage: emptyMessage ?? 'No captured event waveform samples are available.'
  };
}
